import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Stethoscope, XCircle, BookOpen, Calculator, Pill, Sparkles, ArrowRight } from "lucide-react";

export default function PaymentCancelled() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="payment-cancelled-page">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-[#111827]" />
          <span className="font-heading font-bold text-[#111827]">RapidCare SA</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Card className="border-amber-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-8 py-10 text-center border-b border-amber-100">
            <div className="w-16 h-16 rounded-full bg-amber-100 mx-auto flex items-center justify-center mb-4">
              <XCircle className="w-9 h-9 text-amber-600" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#111827]">
              Payment cancelled
            </h1>
            <p className="text-slate-700 mt-3 max-w-lg mx-auto">
              You cancelled before the payment was completed. <strong>No charge was made</strong> and your account is unchanged.
            </p>
          </div>

          <CardContent className="p-8">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 mb-6">
              <div className="font-heading font-semibold text-sm text-[#111827] flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#111827]" />
                Not sure if RapidCare is for you?
              </div>
              <p className="text-sm text-slate-600">
                You can try Premium <strong>free for 7 days</strong> — full access to every protocol, drug and calculator. No card needed.
                If it&apos;s not for you, you stay on the free tier (50 emergency protocols, free forever).
              </p>
            </div>

            <p className="font-heading font-semibold text-xs uppercase tracking-wide text-slate-500 mb-3">
              What you&apos;d unlock
            </p>
            <ul className="grid sm:grid-cols-3 gap-3 mb-8">
              <Feature icon={<BookOpen className="w-4 h-4" />} title="505 protocols" />
              <Feature icon={<Pill className="w-4 h-4" />} title="395 drugs · 759 interactions" />
              <Feature icon={<Calculator className="w-4 h-4" />} title="32 calculators" />
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/pricing" className="flex-1">
                <Button className="bg-[#0B5FFF] hover:bg-[#0A54E0] w-full gap-2" data-testid="cancelled-try-again">
                  Try again <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="outline" onClick={() => nav("/app/dashboard")} className="flex-1" data-testid="cancelled-go-app">
                Continue on free tier
              </Button>
            </div>

            <p className="text-[11px] text-slate-500 mt-6 text-center">
              Trouble with checkout?{" "}
              <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] underline">
                rapidcaresasupport@gmail.com
              </a>
              {" "}— we usually reply within a few hours.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Feature({ icon, title }) {
  return (
    <li className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm">
      <span className="w-6 h-6 rounded-md bg-[#F1F5F9] text-[#111827] flex items-center justify-center shrink-0">{icon}</span>
      <span className="font-medium text-[#111827]">{title}</span>
    </li>
  );
}
